import React, { useMemo, useState } from "react";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { t } from "i18next";

import { Icon } from "components";
import { ToolTipIcon } from "icons";

import { formatUnitData, sortTable } from "../utils";

export const UnitTable = ({ data, setSelectedUnit, selectedUnit, tmlpEnabled, hidePricing }) => {
  const [sortKey, setSortKey] = useState("availability_date");
  const [sortOrder, setSortOrder] = useState("asc");

  const columns = [
    { key: "fp_name", label: t("floorPlan") },
    { key: "unit_name", label: t("unit") },
    { key: "unit_type", label: t("bedroom") },
    { key: "unit_bathroom", label: t("bath") },
    { key: "area", label: t("area") },
    { key: "availability_date", label: t("availability") },
    ...(tmlpEnabled ? [{ key: "total_price", label: "TMLP" }] : !hidePricing ? [{ key: "unit_rent_min", label: "Base Rent" }] : []),
  ];

  const sortedData = useMemo(() => {
    if (sortKey == "availability_date") {
      let mySort = sortTable([...data], sortKey, sortOrder);
      let nullDates = mySort.filter((item) => !item.availability_date);
      let notFound = mySort.filter((item) => item.availability_date === "Inquire for Details");
      let allOther = mySort.filter((item) => item.availability_date != "Inquire for Details" && !!item.availability_date);

      return allOther.concat(notFound, nullDates);
    }

    if (sortKey) {
      let mySort = sortTable([...data], sortKey, sortOrder);
      let nullDates = mySort.filter((item) => item?.[sortKey] === null);
      let allOther = mySort.filter((item) => item?.[sortKey] != null);

      return allOther.concat(nullDates);
    }

    return sortKey ? sortTable([...data], sortKey, sortOrder) : data;
  }, [data, data.length, sortKey, sortOrder]);

  const handleSort = (key) => {
    const newOrder = sortKey === key && sortOrder === "asc" ? "desc" : "asc";
    setSortKey(key);
    setSortOrder(newOrder);
  };

  const renderSortIcon = (key) => {
    if (sortKey === key) {
      return sortOrder === "asc" ? (
        <Icon iconType="TopArrow" className="hayley-ml-1 hayley-h-3 hayley-w-3 hayley-text-secondary" />
      ) : (
        <Icon iconType="ArrowDown" className="hayley-ml-1 hayley-h-3 hayley-w-3 hayley-text-secondary" />
      );
    }

    return null;
  };

  return (
    <TableContainer component={Paper} style={{ maxHeight: "100%", border: "1px solid #3333" }}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            {columns.map(({ key, label }) => (
              <TableCell key={key} onClick={() => handleSort(key)} className="hayley-cursor-pointer">
                <div className="hayley-flex hayley-items-center hayley-gap-1">
                  {label}
                  {key === "total_price" && (
                    <Tooltip
                      title="Total monthly Leasing Price"
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "#333333",
                            color: "#ffffff",
                            fontSize: "14px",
                            fontFamily: "Open Sans",
                            fontWeight: 400,
                            padding: "6px 10px",
                            lineHeight: "20px",
                            "& .MuiTooltip-arrow": {
                              color: "#333333",
                            },
                          },
                        },
                      }}
                    >
                      <div className="hayley-flex hayley-cursor-pointer hayley-items-center">
                        <ToolTipIcon classes="hayley-h-4 hayley-w-4" fill="#666666" />
                      </div>
                    </Tooltip>
                  )}
                  {renderSortIcon(key)}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData?.map((v) => {
            const isSelectedUnit = selectedUnit?.id === v?.id;
            const formattedData = formatUnitData(v);

            return (
              <TableRow
                data-testid="table-row"
                key={v?.id}
                className={`hayley-group hayley-cursor-pointer hover:hayley-bg-gray-100 ${isSelectedUnit ? "hayley-bg-gray-100" : ""}`}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                onClick={() => setSelectedUnit(v)}
              >
                <TableCell component="th" scope="v" className={`${isSelectedUnit ? "hayley-font-semibold" : ""}`}>
                  {formattedData.fp_name}
                </TableCell>
                <TableCell component="th" scope="v" className={`${isSelectedUnit ? "hayley-font-semibold" : ""}`}>
                  {formattedData.name}
                </TableCell>
                <TableCell> {formattedData.unitType}</TableCell>
                <TableCell> {formattedData?.unitBathroom}</TableCell>
                <TableCell>{formattedData.areaFt}</TableCell>
                <TableCell>{formattedData?.availabilityDate}</TableCell>
                {tmlpEnabled && (
                  <TableCell>
                    <div className=" hayley-flex hayley-items-center hayley-justify-between">
                      <span>{formattedData.totalPrice} </span>
                      <div className={`hayley-pl-1 group-hover:hayley-visible ${isSelectedUnit ? "hayley-visible" : "hayley-invisible"}`}>
                        <Icon iconType="Arrow" className="hayley-h-3 hayley-w-3 hayley-rotate-180 hayley-text-secondary" />
                      </div>
                    </div>
                  </TableCell>
                )}
                {!tmlpEnabled && !hidePricing && (
                  <TableCell>
                    <div className=" hayley-flex hayley-items-center hayley-justify-between">
                      <span>{formattedData.unitRentMin} </span>
                      <div className={`hayley-pl-1 group-hover:hayley-visible ${isSelectedUnit ? "hayley-visible" : "hayley-invisible"}`}>
                        <Icon iconType="Arrow" className="hayley-h-3 hayley-w-3 hayley-rotate-180 hayley-text-secondary" />
                      </div>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
